import { Dialog as ChakraDialog, Portal } from '@chakra-ui/react';
import React, { forwardRef } from 'react';

import CloseButton from 'ui/CloseButton';

// This ref context is used for the DialogContent to be able aware of its parent
// dialog that we can pass to the Portal as a container ref, which enables proper
// stacking and backdrop behavior of the dialog.
// See https://github.com/sankeyart/sankeyart/issues/2081
const DialogRefContext =
  React.createContext<React.RefObject<HTMLDivElement> | null>(null);

const DialogContent = forwardRef<HTMLDivElement, ChakraDialog.ContentProps>(
  ({ children, ...rest }, ref) => {
    const parentDialogRef = React.useContext(DialogRefContext);
    const dialogRef = React.useRef<HTMLDivElement>(null);
    return (
      <Portal container={parentDialogRef ?? undefined}>
        <ChakraDialog.Backdrop />
        <ChakraDialog.Positioner>
          <ChakraDialog.Content
            ref={dialogRef}
            asChild={false}
            h="auto"
            {...rest}
          >
            <DialogRefContext.Provider value={dialogRef}>
              {children}
            </DialogRefContext.Provider>
            <ChakraDialog.CloseTrigger
              position="absolute"
              top="2"
              insetEnd="2"
              asChild
            >
              <CloseButton />
            </ChakraDialog.CloseTrigger>
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </Portal>
    );
  }
);

const DialogHeader = forwardRef<HTMLDivElement, ChakraDialog.HeaderProps>(
  ({ children, ...props }, ref) => (
    <ChakraDialog.Header
      ref={ref}
      bg="green.800"
      color="white"
      fontSize="lg"
      px={4}
      py={3}
      {...props}
    >
      <ChakraDialog.Title>{children}</ChakraDialog.Title>
    </ChakraDialog.Header>
  )
);

const DialogBody = forwardRef<HTMLDivElement, ChakraDialog.BodyProps>(
  ({ children, ...props }, ref) => (
    <ChakraDialog.Body
      ref={ref}
      color="gray.800"
      fontSize="md"
      px={4}
      py={4}
      {...props}
    >
      {children}
    </ChakraDialog.Body>
  )
);

const DialogFooter = forwardRef<HTMLDivElement, ChakraDialog.FooterProps>(
  ({ children, ...props }, ref) => (
    <ChakraDialog.Footer ref={ref} gap={3} px={4} pt={2} pb={4} {...props}>
      {children}
    </ChakraDialog.Footer>
  )
);

const Dialog = {
  Root: ChakraDialog.Root,
  Trigger: ChakraDialog.Trigger,
  Content: DialogContent,
  Header: DialogHeader,
  Body: DialogBody,
  Footer: DialogFooter,
};

export default Dialog;
